import React from 'react';
import { Alert, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getBlockedActionLines,
  type WarehouseBlockedAction,
  type WarehouseDocumentNamespace,
} from './blockedActions';

interface BlockedActionsAlertProps {
  blockedActions: WarehouseBlockedAction[] | undefined;
  ns: WarehouseDocumentNamespace;
  /** Code→i18n key map of the namespace (PZ_ERROR_CODE_KEYS / WZ_ERROR_CODE_KEYS). */
  reasonKeys: Record<string, string>;
}

/**
 * Proactive explanation on the document card: one line per unavailable
 * action, in the same words the toast would use after a rejected call (documents
 * named by an earlier line are not repeated). The
 * technical code is exposed only in a hover tooltip on the action name so
 * an operator can quote it to support without it cluttering the sentence.
 */
const BlockedActionsAlert: React.FC<BlockedActionsAlertProps> = ({ blockedActions, ns, reasonKeys }) => {
  const { t } = useTranslation();
  if (!blockedActions || blockedActions.length === 0) return null;

  return (
    <Alert
      type="warning"
      showIcon
      className="warehouse-blocked-actions"
      message={t(`${ns}:blocked.title`)}
      description={(
        <ul className="warehouse-blocked-actions__list">
          {getBlockedActionLines(blockedActions, t, ns, reasonKeys).map(({ blocked, message }) => (
            <li key={`${blocked.action}:${blocked.code}`}>
              <Tooltip title={t(`${ns}:blocked.technicalCode`, { code: blocked.code })}>
                <Typography.Text strong>{t(`${ns}:blocked.actions.${blocked.action}`)}</Typography.Text>
              </Tooltip>
              {': '}
              {message}
            </li>
          ))}
        </ul>
      )}
    />
  );
};

export default BlockedActionsAlert;
